import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { CustomTable, ErrorModal, Popover, SearchBar, Status } from '../../components/shared';
import { statesData, userColumnData, userTableColumnExtensions } from '../../utils/mockUpData';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { allUsersAPI } from '../../redux/features/users/userSlice';
import { capitalizeFirstLetter, formatDate } from '../../utils/validator';
import { useSearchParams } from 'react-router-dom';
import { accountStatus, TableActions } from '../../components/shared/StatusUtility';

import { EditAbleActions, UserUtils } from './utils';
import useOnClickOutside from '../../utils/hooks/useOnClickOutside';
import Filters from '../../components/shared/Filters';
import { State } from '../../utils/types';

const Users = () => {
  const { loading, error, allUsers } = useAppSelector(state => state.allUsers);
  const dispatch = useAppDispatch();

  // States
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingIds, setEditingIds] = useState<string[] | null>([]);
  const [activePopover, setActivePopover] = useState<number | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<string>('');
  const [statesList, setStatesList] = useState<State[]>(statesData);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedState, setSelectedState] = useState<string>('');
  const [searchName, setSearchName] = useSearchParams({
    search: '',
    state: ''
  });
  const [filters, setFilters] = useState({
    Users: [
      {
        title: 'Pro',
        value: false
      },
      {
        title: 'Customer',
        value: false
      }
    ]
  });
  // Get Query Pararms Value
  const searchedValue = searchName.get('search');

  const rowData =
    allUsers?.data?.length > 0
      ? allUsers?.data?.map((item: any, index: number) => {
          const profile = item?.profile;
          return {
            index: index + 1,
            id: item?._id,
            name:
              profile !== '' && profile !== null && profile !== undefined
                ? capitalizeFirstLetter(`${profile?.firstName} ${profile?.lastName}`)
                : 'N/A',
            role: item?.role ? capitalizeFirstLetter(item?.role?.role) : '',
            phoneNumber: item?.phoneNumber ? item?.phoneNumber : '',
            email: item?.email ? item?.email : '',
            status: item?.isSuspended,
            isEmailVerified: item?.isEmailVerified ? item?.isEmailVerified : '',
            createdAt: item?.createdAt ? formatDate(item?.createdAt, 'MM/DD/YY, hh:mm A') : '',
            deletedAt: item?.deletedAt ? formatDate(item?.deletedAt, 'MM/DD/YY, hh:mm A') : 'N/A',
            updatedAt: item?.isSuspended
              ? item?.updatedAt
                ? formatDate(item?.updatedAt, 'MM/DD/YY, hh:mm A')
                : 'N/A'
              : 'N/A',
            isDeleted: item?.isDeleted ?? false,
            profile: item?.profile ? item?.profile : null,
            completeItem: item
          };
        })
      : [];
  // Toggle Popover
  const handleToggle = (index: number) => {
    setActivePopover(prev => (prev === index ? null : index));
  };

  // Data Providers
  const dataProviders = [
    {
      columnName: ['isEmailVerified'],
      func: (restProps: any) => Status(restProps?.row?.isEmailVerified, 'Verified', 'Verified')
    },
    {
      columnName: ['status'],
      func: (restProps: any) => accountStatus(restProps?.row?.status, 'Suspended', 'Active')
    },
    {
      columnName: ['action'],
      func: (restProps: any) =>
        editingIds?.includes(restProps?.row?.id) ? (
          <EditAbleActions
            row={restProps}
            setEditingIds={setEditingIds}
            recallList={searchedValue ? searchUsers : handleCallUserList}
          />
        ) : (
          <TableActions
            children={() => (
              <UserUtils
                restProps={restProps}
                recallList={searchedValue ? searchUsers : handleCallUserList}
                onEdit={() => setEditingIds((prev: any) => [...prev, restProps?.row?.id])}
              />
            )}
            restProps={restProps}
            isActive={activePopover === restProps?.row?.index}
            onToggle={handleToggle}
            height='max-content'
            ref={popoverRef}
          />
        )
    }
  ];

  // Handle Filters
  const onChangeFilters = (title: string, item: any, index: number) => {
    if (title === 'clearAll') {
      setFilters({
        Users: filters.Users.map(b => ({ ...b, value: false }))
      });
      setState('');
      setSelectedState('');
      handleCallUserList();
      setIsFiltersOpen(false);
      return;
    }
    if (title === 'Users') {
      setFilters(prevFilters => {
        const users = prevFilters.Users.map((b, i) => ({
          ...b,
          value: i === index ? !item?.value : false
        }));
        return { Users: users };
      });
      const pro = index === 0 && !item?.value ? 'pro' : '';
      const customer = index === 1 && !item?.value ? 'customer' : '';
      const type = pro || customer;
      dispatch(
        allUsersAPI({
          type: type
        })
      );
    }
  };

  // InlineSearch Fn
  const searchUsers = () => {
    if (!searchedValue) {
      handleCallUserList();
    }
    dispatch(allUsersAPI({ search: searchedValue }));
  };

  // Handle Call User List
  const handleCallUserList = () => {
    dispatch(allUsersAPI({}));
    setActivePopover(null);
  };

  // HANDLE STATE SEARCH
  const onStateSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(event.target.value);
    const searched = statesData.filter((item: State) =>
      item.name.toLowerCase().includes(event.target.value?.toLowerCase())
    );
    setStatesList(searched);
    // ONCLEAR
    if (event.target.value === '') {
      handleCallUserList();
      setState('');
      setSelectedState('');
    }
  };

  // ON SELECT STATE
  const onSelectState = (item: State) => {
    setSelectedState(item.name);
    setState(item.name);
    dispatch(allUsersAPI({ state: item.name }));
    setStatesList([]);
  };

  // HANDLE KEYBOARD EVENTS
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!statesList.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < statesList.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : statesList.length - 1));
    } else if (e.key === 'Enter') {
      onSelectState(statesList[highlightedIndex]);
    }
  };

  // UseEffect
  useEffect(() => {
    if (searchedValue) {
      searchUsers();
    } else {
      handleCallUserList();
    }
    // eslint-disable-next-line
  }, [dispatch, searchedValue]);

  //useOutSideClick
  useOnClickOutside(popoverRef, () => setActivePopover(null));

  return (
    <React.Fragment>
      <div className='w-full h-full flex flex-col gap-2 p-3'>
        <div className='flex gap-2'>
          <SearchBar
            value={searchedValue || ''}
            onChange={e => {
              e.target.value =
                e.target.value.charAt(0) === ' ' ? '' : e.target.value.replace(/ +$/, ' ');
              if (e.target.value === '') {
                setSearchName();
                handleCallUserList();
              } else {
                setSearchName({
                  search: e.target.value
                });
              }
            }}
            onClear={() => {
              setSearchName();
              handleCallUserList();
            }}
            onSearch={searchUsers}
          />
          <div>
            <Popover
              menuHeading={'Filters'}
              dropdownOpen={isFiltersOpen}
              setDropdownOpen={setIsFiltersOpen}
              active={
                filters.Users[0].value ||
                filters.Users[1].value ||
                Boolean(state) ||
                Boolean(selectedState) ||
                false
              }
              className={'!w-[280px]'}
            >
              <Filters options={filters} onChange={onChangeFilters} />
              <div className='flex flex-col px-3 pt-0 pb-3 space-y-1'>
                <span className='text-xs'>States</span>
                <input
                  name='state'
                  value={state}
                  onChange={onStateSearch}
                  onKeyDown={handleKeyDown}
                  placeholder='Search state...'
                  className='border border-primary rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary transition-all duration-300 ease-in-out'
                />

                {state?.length > 0 && (
                  <div className='flex flex-col gap-2 pt-1'>
                    {statesList.length > 0 ? (
                      statesList.slice(0, 8).map((item: State, index: number) => {
                        const isActive = index === highlightedIndex;
                        return (
                          <p
                            key={index}
                            onClick={() => onSelectState(item)}
                            className={`transition-all duration-200 ease-in-out rounded-md cursor-pointer px-1 py-1 ${
                              isActive ? 'bg-primary text-white' : 'hover:bg-gray-200'
                            }`}
                          >
                            {item.name}
                          </p>
                        );
                      })
                    ) : !Boolean(selectedState) ? (
                      <p className='text-sm transition-all duration-300 ease-in-out rounded-md cursor-pointer hover:bg-gray-300 px-1 py-1'>
                        No State found
                      </p>
                    ) : null}
                  </div>
                )}
              </div>
            </Popover>
          </div>
        </div>
        <ErrorModal showModal={Boolean(error)} errorMsg={error} />
        {/* Table */}
        <div className=''>
          <CustomTable
            rows={rowData?.length > 0 ? rowData : []}
            columns={userColumnData}
            tableColumnExtensions={userTableColumnExtensions}
            dataProviders={dataProviders}
            selectedIds={selectedIds}
            setSelectedIds={(selectedIds: SetStateAction<never[]>) => setSelectedIds(selectedIds)}
            componentHeader={true}
            loading={loading}
            isRowsEditable={true}
            editingIds={editingIds}
            setEditingIds={setEditingIds}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default Users;
